import {
  Button,
  Col,
  Descriptions,
  DescriptionsProps,
  Form,
  Input,
  Result,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
  Typography,
} from 'antd';
import Page from 'components/page';
import useGetAllRoles from 'hooks/data/useGetAllRoles';
import useGetGroup from 'hooks/data/useGetGroup';
import useUpdateGroup from 'hooks/update/useUpdateGroup';
import useToastContext from 'hooks/useToastContext';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { camelCaseObjectToSnakeCase } from 'utils/camelCaseToSnakeCase';
import { HttpStatusCode } from '../../constants';

type EditGroupFormInputs = {
  name: string;
  roles: string[];
};

const GroupPage = () => {
  const { groupId } = useParams<{ groupId }>();
  const { group, isLoading, isError } = useGetGroup(groupId);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState<boolean>(false);
  const toast = useToastContext();
  const { updateGroupLoading, updateGroup } = useUpdateGroup();
  const { roles } = useGetAllRoles();

  const items: DescriptionsProps['items'] = [
    {
      key: '1',
      label: 'ID',
      children: group?.id,
    },
    {
      key: '2',
      label: 'Nombre',
      children: !editMode ? (
        group?.name
      ) : (
        <Form.Item<EditGroupFormInputs>
          name="name"
          initialValue={group?.name}
          rules={[{ required: true, message: 'Por favor ingresar un nombre de grupo.' }]}
        >
          <Input />
        </Form.Item>
      ),
    },
    {
      key: '3',
      label: 'Path',
      children: group?.path,
    },
  ];

  return (
    <Page title={`Grupo ${group?.name || ''}`}>
      <Row>
        <Col span={24}>
          <Skeleton loading={isLoading}>
            {isError && (
              <Result
                status="warning"
                title="Hubo un problema al cargar la pagina."
                extra={
                  <Button type="primary" key="console" onClick={() => navigate(0)}>
                    Reintentar
                  </Button>
                }
              />
            )}
            {!isError && (
              <>
                <Form
                  form={form}
                  onFinish={async (data) => {
                    try {
                      const res = await updateGroup(
                        group?.id,
                        camelCaseObjectToSnakeCase({
                          ...data,
                          roles: roles
                            .filter((role) => data.roles.includes(role.id))
                            .map((role) => ({ id: role.id, name: role.name })),
                        })
                      );
                      if ([HttpStatusCode.NoContent, HttpStatusCode.Ok].includes(res.status)) {
                        toast.success('Operacion exitosa!', 'El grupo se ha actualizado con exito.');
                        form.resetFields();
                        setEditMode(false);
                      } else {
                        throw Error('Status code different 204');
                      }
                    } catch (e) {
                      toast.error('Error!', 'El grupo no se ha actualizado, por favor reintentar nuevamente.');
                      console.error(e);
                    }
                  }}
                >
                  <Descriptions
                    title={<Typography.Title level={2}>Detalles</Typography.Title>}
                    layout="horizontal"
                    bordered
                      extra={
                        editMode ? (
                          <Space>
                            <Button
                              type="text"
                              danger
                              onClick={() => {
                                form.resetFields();
                                setEditMode(false);
                              }}
                            >
                              Cancelar
                            </Button>
                            <Button type="primary" loading={updateGroupLoading} onClick={() => form.submit()}>
                              Guardar
                            </Button>
                          </Space>
                        ) : (
                          <Button type="primary" onClick={() => setEditMode(true)}>
                            Editar
                          </Button>
                        )
                      }
                    items={items}
                  />

                  <Typography.Title level={2} style={{ marginTop: '1rem' }}>
                    Roles
                  </Typography.Title>
                  {!editMode ? (
                    <Table
                      scroll={{ x: true }}
                      loading={isLoading}
                      columns={[
                        {
                          dataIndex: 'id',
                          title: 'ID',
                          key: 'id',
                        },
                        {
                          dataIndex: 'name',
                          title: 'Nombre',
                          key: 'name',
                        },
                        {
                          dataIndex: 'description',
                          title: 'Descripcion',
                          key: 'description',
                        },
                      ]}
                      dataSource={group?.roles}
                      pagination={{
                        total: group?.roles?.length,
                        pageSizeOptions: ['10', '25', '50', '100'],
                      }}
                    />
                  ) : (
                    <Form.Item<EditGroupFormInputs>
                      name="roles"
                      initialValue={group?.roles?.map((role) => role.id)}
                      rules={[
                        { required: true, message: 'Por favor seleccionar roles.' },
                        { type: 'array', min: 1, message: 'Seleccionar uno o mas roles.' },
                      ]}
                    >
                      <Select
                        mode="multiple"
                        options={roles?.map((role) => ({
                          label: role?.name,
                          value: role?.id,
                        }))}
                      />
                    </Form.Item>
                  )}
                </Form>
              </>
            )}
          </Skeleton>
        </Col>
      </Row>
    </Page>
  );
};

export default GroupPage;
